import { Component, effect, ElementRef, ViewChild } from '@angular/core';
import { HTMLReport } from '@app/@shared/services/hygraph/hygraph.models';
import { PreviousPageService } from '@app/@shared/services/previous-page.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { OptionGroup, SocialOption } from '../../generic-toolbar/option-model';
import { ActivatedRoute, Router } from '@angular/router';
import { HygraphService } from '@app/@shared/services/hygraph/hygraph.service';
import { MetaService } from '@app/@shared/services/meta.service';
import { TelemetryEvent } from '@app/@shared/models/telemetry-event';
import { TelemetryEventName } from '@app/@shared/enums';
import { StatelessBehavior, ToggleBehavior } from '../../generic-toolbar/state-behavior-model';
import { Icon } from '../../generic-toolbar/icons';
import { ShareItemModalComponent } from '../../share-item-modal/share-item-modal.component';
import { DialogService } from '@app/@shared/services/dialog/dialog.service';
import { AuthenticationService } from '@app/auth';
import { isUserAllowedToPreviewHygraph } from '@app/@shared/utils/hygraph-preview-role-selector';
import { catchError, concatMap, map } from 'rxjs';

import { toSignal } from '@angular/core/rxjs-interop';
import { isTablet } from '@app/group/helpers/is-tablet';
import { HYGRAPH_CONTENT_STAGES } from '@app/@shared/consts/hygraph-preview';

@UntilDestroy()
@Component({
    selector: 'app-sectioned-blog-viewer',
    templateUrl: './sectioned-blog-viewer.component.html',
    styleUrl: './sectioned-blog-viewer.component.scss',
    standalone: false
})
export class SectionedBlogViewerComponent {
  @ViewChild('hygraph', { static: false, read: ElementRef }) hygraphElement: ElementRef;

  htmlReport: HTMLReport;
  lockedMessage = 'Full access to this section requires a subscription';
  showDownloadButton = false;
  toolbarOptions: OptionGroup[];
  blogId: string;
  reportTitle: string;
  isContentLoading = true;
  selectedStage: string = HYGRAPH_CONTENT_STAGES.PUBLISHED;

  fitWidth = false;
  isTablet = toSignal(isTablet());

  constructor(
    public readonly prevPage: PreviousPageService,
    private readonly hygraphService: HygraphService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly metaService: MetaService,
    private readonly dialogService: DialogService,
    private readonly authenticationService: AuthenticationService,

  ) {
    effect(() => {
      this.toolbarOptions = this.createToolbarOptions()
    })
  }

  ngOnInit(): void {
    this.toolbarOptions = this.createToolbarOptions();

    this.route.params.subscribe((params) => {
      this.blogId = params.blogId;
      if (this.blogId) {
        this.initializeContent();
      }
    });
  }

  public onClickBack() {
    this.prevPage.navigateBack();
  }

  public handleStageChange(stage: string): void {
    // Update the selected stage and fetch data for the new stage
    this.selectedStage = stage;
    this.fetchData(this.blogId, stage);
  }

  public share() {
    this.dialogService.open(
      ShareItemModalComponent,
      { windowClass: 'shareModalClass' },
      { isDocumentShareable: false }
    );
  }

  private initializeContent(): void {
    // Always try to load Published first
    this.selectedStage = HYGRAPH_CONTENT_STAGES.PUBLISHED;
    this.tryLoadPublishedWithPreviewFallback();
  }

  private tryLoadPublishedWithPreviewFallback(): void {
    this.isContentLoading = true;
    this.hygraphService.getBlog(this.blogId, HYGRAPH_CONTENT_STAGES.PUBLISHED)
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            // Published not available, check if user has preview access and try preview
            return this.authenticationService.me().pipe(
              concatMap((user) => {
                const hasPreviewAccess = isUserAllowedToPreviewHygraph(user);
                if (hasPreviewAccess) {
                  // Try preview as fallback
                  return this.hygraphService.getBlog(this.blogId, HYGRAPH_CONTENT_STAGES.PREVIEW).pipe(
                    map((previewResp) => {
                      // Preview exists - load it and update dropdown to show preview
                      this.selectedStage = HYGRAPH_CONTENT_STAGES.PREVIEW;
                      this.htmlReport = previewResp;
                      this.reportTitle = this.htmlReport.header.title;
                      this.isContentLoading = false;
                      this.postTelemetryEvent(this.blogId, previewResp);
                      return previewResp;
                    }),
                    catchError(() => {
                      // Neither published nor preview available
                      throw error;
                    })
                  );
                } else {
                  // User doesn't have preview access and published doesn't exist
                  throw error;
                }
              })
            );
          }
          throw error;
        })
      )
      .subscribe(
        (hygraphResp) => {
          if (hygraphResp) {
            // Successfully loaded published content
            this.htmlReport = hygraphResp;
            this.reportTitle = this.htmlReport.header.title;
            this.isContentLoading = false;
            this.postTelemetryEvent(this.blogId, hygraphResp);
          }
        },
        (error) => {
          this.isContentLoading = false;
          if (error.status === 404) {
            this.dialogService.openError({
              title: 'Content not available',
              closeButtonLabel: 'Close',
              body: 'No published or preview version available.'
            });
          }
        }
      );
  }

  private fetchData(blogId: string, stageName?: string): void {
    this.isContentLoading = true;
    this.hygraphService.getBlog(blogId, stageName)
      .subscribe((hygraphResp) => {
        this.htmlReport = hygraphResp;
        this.reportTitle = this.htmlReport.header.title;
        this.isContentLoading = false;
        this.postTelemetryEvent(blogId, hygraphResp);
      }, (error) => {
        this.isContentLoading = false;
        if(error.status === 404) {
          this.dialogService.openError({
            title: 'Content not available',
            closeButtonLabel: 'Close',
            body: 'Select another version.'
          });
        }
      });
  }

  private createToolbarOptions(): OptionGroup[] {
    const optionGroups: OptionGroup[] = [];
    const options: SocialOption[] = [];

    if(!this.isTablet()) {
      const tooltip = 'Toggle Width';
      options.push(
        new SocialOption('fit-width', { click: this.toggleFullWidth.bind(this) }, new ToggleBehavior(Icon.HYGRAPH_FIT_WIDTH, tooltip, Icon.HYGRAPH_DEFAULT_WIDTH, tooltip))
      )
    }

    options.push(
      new SocialOption('share', { click: this.share.bind(this) }, new StatelessBehavior(Icon.IMAGE_SHARE, 'Share'))
    );

    optionGroups.push(new OptionGroup(options));
    return optionGroups;
  }

  private toggleFullWidth() {
    this.fitWidth = !this.fitWidth;
  }

  private postTelemetryEvent(blogId: string, hygraphResp: HTMLReport) {
    const categoryTags = hygraphResp.header?.categoryTags || [];
    const tags = categoryTags.map((tag) => tag.key);
    const payload = {
      BlogId: blogId,
      Title: hygraphResp.header.title,
      Author: hygraphResp.header?.authors ? hygraphResp.header.authors[0].name : '',
      Tags: tags,
      createdDate: hygraphResp.publishDate
    };
    const event = new TelemetryEvent(TelemetryEventName.EVENT_TISAPP_BLOG_VIEW, payload);

    this.metaService.postTelemetryEvent(event).subscribe();
  }

}
